import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  AppBar,
  Toolbar,
  Slide,
  Tooltip,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useDebounce } from '@/hooks/useDebounce';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  useGlobalSearch,
  type GlobalSearchResult,
  type GlobalSearchResultType,
} from '@/hooks/useGlobalSearch';
import { forwardRef } from 'react';

interface SearchResult {
  id: string;
  type: GlobalSearchResultType;
  title: string;
  subtitle: string;
  link: string;
}

const typeIcons: Record<GlobalSearchResultType, React.ReactNode> = {
  person: <PersonIcon fontSize="small" />,
  company: <BusinessIcon fontSize="small" />,
  vehicle: <DirectionsCarIcon fontSize="small" />,
  work_order: <AssignmentIcon fontSize="small" />,
};

// The backend returns no href — the frontend derives the route from type + id.
function buildHref(result: Pick<GlobalSearchResult, 'type' | 'id'>): string {
  switch (result.type) {
    case 'person':
      return `/parties/persons/${result.id}`;
    case 'company':
      return `/parties/companies/${result.id}`;
    case 'vehicle':
      return `/vehicles?id=${result.id}`;
    case 'work_order':
      return `/workorders/${result.id}`;
  }
}

// Order entity-type groups in the dropdown: vehicles first (the primary
// registration-number use case), then customers, then work orders.
const GROUP_ORDER: Record<GlobalSearchResultType, number> = {
  vehicle: 0,
  person: 1,
  company: 2,
  work_order: 3,
};

// Type labels (badges) and group headers are resolved inside SearchAutocomplete using t()

const SlideDown = forwardRef(function SlideDown(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export function SearchAutocomplete({
  autoFocus,
  onSelect,
}: {
  autoFocus?: boolean;
  onSelect?: () => void;
}) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const typeLabels: Record<GlobalSearchResultType, string> = {
    person: t('searchCategories.person'),
    company: t('searchCategories.company'),
    vehicle: t('searchCategories.vehicle'),
    work_order: t('searchCategories.workOrder'),
  };
  const typeGroupLabels: Record<GlobalSearchResultType, string> = {
    vehicle: t('searchCategories.vehicles'),
    person: t('searchCategories.persons'),
    company: t('searchCategories.companies'),
    work_order: t('searchCategories.workOrders'),
  };
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const debouncedQuery = useDebounce(query, 300);

  // Unified search endpoint. Disabled below the minimum length, so short input
  // makes no requests; previous data is kept while refetching to avoid flicker.
  const { data, isFetching } = useGlobalSearch(debouncedQuery);

  // Map to display rows and order by entity-type group so MUI's groupBy renders
  // one header per type. Stable sort preserves the backend's within-group
  // relevance order (exact matches first).
  const results: SearchResult[] = (data?.results ?? [])
    .map((r) => ({
      id: r.id,
      type: r.type,
      title: r.title,
      subtitle: r.subtitle ?? '',
      link: buildHref(r),
    }))
    .sort((a, b) => GROUP_ORDER[a.type] - GROUP_ORDER[b.type]);

  const handleSelect = (_event: React.SyntheticEvent, value: string | SearchResult | null) => {
    if (value && typeof value !== 'string') {
      navigate(value.link);
      setQuery('');
      setOpen(false);
      onSelect?.();
    }
  };

  const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
    setQuery(value);
    setOpen(value.length >= 2);
  };

  return (
    <Autocomplete
      freeSolo
      autoHighlight
      open={open && results.length > 0}
      onClose={() => setOpen(false)}
      options={results}
      loading={isFetching}
      filterOptions={(x) => x}
      groupBy={(option) => (typeof option === 'string' ? '' : option.type)}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
      onChange={handleSelect}
      onInputChange={handleInputChange}
      inputValue={query}
      noOptionsText={
        query.length < 2
          ? t('searchCategories.minChars')
          : t('noResults')
      }
      renderGroup={(params) => (
        <li key={params.key}>
          <Typography
            component="div"
            variant="caption"
            sx={{
              position: 'sticky',
              top: 0,
              px: 2,
              py: 0.5,
              fontWeight: 600,
              color: 'text.secondary',
              bgcolor: 'background.paper',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {typeGroupLabels[params.group as GlobalSearchResultType] ?? params.group}
          </Typography>
          <Box component="ul" sx={{ p: 0 }}>
            {params.children}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          placeholder={t('search') + '...'}
          size="small"
          // Opts back out of the theme's standard default (#2148): the sx below
          // targets .MuiOutlinedInput-root, and the bar's filled look depends on it.
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'action.hover',
              '& fieldset': {
                border: 'none',
              },
              '&:hover fieldset': {
                border: 'none',
              },
              '&.Mui-focused fieldset': {
                border: 'none',
              },
            },
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} aria-hidden="true" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {isFetching ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
          inputProps={{
            ...params.inputProps,
            'aria-label': t('search'),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={`${option.type}-${option.id}`}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
              {typeIcons[option.type]}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 0 }} noWrap>
                  {option.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    bgcolor: 'action.hover',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.65rem',
                    flexShrink: 0,
                  }}
                >
                  {typeLabels[option.type]}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.subtitle}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      sx={{ flex: 1, maxWidth: 400 }}
    />
  );
}

export function GlobalSearch() {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  if (isMobile) {
    return (
      <>
        <Tooltip title={t('search')}>
          <IconButton
            onClick={() => setMobileSearchOpen(true)}
            aria-label={t('search')}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
        <Dialog
          fullScreen
          open={mobileSearchOpen}
          onClose={() => setMobileSearchOpen(false)}
          TransitionComponent={SlideDown}
        >
          <AppBar sx={{ position: 'relative' }} elevation={0}>
            <Toolbar>
              <Tooltip title={t('close')}>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => setMobileSearchOpen(false)}
                  aria-label={t('close')}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
              <Typography sx={{ ml: 1, flex: 1 }} variant="h6">
                {t('search')}
              </Typography>
            </Toolbar>
          </AppBar>
          <Box sx={{ p: 2 }}>
            <SearchAutocomplete
              autoFocus
              onSelect={() => setMobileSearchOpen(false)}
            />
          </Box>
        </Dialog>
      </>
    );
  }

  return <SearchAutocomplete />;
}
